import { createSlice } from "@reduxjs/toolkit";
import {
    fetchDealTypes,
    fetchCategories,
    fetchDealTerms,
    createDealTerm,
    updateDealTerm,
    deleteDealTerm,
    createDeal,
    updateDeal,
    getDealById,
    getInfluencerDealById,
    fetchDealAnalytics,
    createDealDraft,
    updateDealDraft,
    getDealDraftById,
    deleteDealDraft,
    fetchMerchantDeals,
} from "./DealsThunk";
import { status } from "../../_constants/common";

const dealsSlice = createSlice({
    name: "deals",
    initialState: {
        dealTypes: {
            status: null,
            data: {},
        },
        categories: {
            status: null,
            data: {},
        },
        dealTerms: {
            status: null,
            data: {},
        },
        createTerm: {
            status: null,
            data: {},
        },
        updateTerm: {
            status: null,
            data: {},
        },
        deleteTerm: {
            status: null,
            data: {},
        },
        createDeal: {
            status: null,
            data: {},
        },
        updateDeal: {
            status: null,
            data: {},
        },
        dealDetails: {
            status: null,
            data: null,
        },
        dealAnalytics: {
            status: null,
            data: null,
        },
        // List of draft deals for the dashboard "Draft Deals" tab.
        drafts: {
            status: null,
            data: [],
        },
        // Persisted draft progress so multi-step deal creation survives reloads.
        // formSnapshot holds non-File data in localStorage.
        // Draft image files are persisted separately in IndexedDB.
        draft: {
            draftId: null,
            currentStep: 1,
            selectedDealTypeId: null,
            formSnapshot: null,
            imagePreview: null,
            existingImageUrl: null,
            saveStatus: null,
        },
    },
    reducers: {
        clearDealTypes: (state) => {
            state.dealTypes = { status: null, data: {} };
        },
        clearCategories: (state) => {
            state.categories = { status: null, data: {} };
        },
        clearDealTerms: (state) => {
            state.dealTerms = { status: null, data: {} };
        },
        clearCreateDeal: (state) => {
            state.createDeal = { status: null, data: {} };
        },
        clearUpdateDeal: (state) => {
            state.updateDeal = { status: null, data: {} };
        },
        clearDealDetails: (state) => {
            state.dealDetails = { status: null, data: null };
        },
        setDraftSnapshot: (state, { payload }) => {
            state.draft = { ...state.draft, ...payload };
        },
        clearDraftSnapshot: (state) => {
            state.draft = {
                draftId: null,
                currentStep: 1,
                selectedDealTypeId: null,
                formSnapshot: null,
                imagePreview: null,
                existingImageUrl: null,
                saveStatus: null,
            };
        },
    },
    extraReducers: (builder) => {
        builder
            // fetchDealTypes
            .addCase(fetchDealTypes.pending.toString(), (state, action) => {
                return {
                    ...state,
                    dealTypes: {
                        status: status.IN_PROGRESS,
                        data: {},
                    },
                };
            })
            .addCase(fetchDealTypes.fulfilled.toString(), (state, { payload }) => {
                return {
                    ...state,
                    dealTypes: {
                        status: status.SUCCESS,
                        data: payload,
                    },
                };
            })
            .addCase(fetchDealTypes.rejected.toString(), (state, action) => {
                return {
                    ...state,
                    dealTypes: {
                        status: status.FAILURE,
                        data: action.error,
                    },
                };
            })
            // fetchCategories
            .addCase(fetchCategories.pending.toString(), (state, action) => {
                return {
                    ...state,
                    categories: {
                        status: status.IN_PROGRESS,
                        data: {},
                    },
                };
            })
            .addCase(fetchCategories.fulfilled.toString(), (state, { payload }) => {
                return {
                    ...state,
                    categories: {
                        status: status.SUCCESS,
                        data: payload,
                    },
                };
            })
            .addCase(fetchCategories.rejected.toString(), (state, action) => {
                return {
                    ...state,
                    categories: {
                        status: status.FAILURE,
                        data: action.error,
                    },
                };
            })
            // fetchDealTerms
            .addCase(fetchDealTerms.pending.toString(), (state, action) => {
                return {
                    ...state,
                    dealTerms: {
                        status: status.IN_PROGRESS,
                        data: {},
                    },
                };
            })
            .addCase(fetchDealTerms.fulfilled.toString(), (state, { payload }) => {
                return {
                    ...state,
                    dealTerms: {
                        status: status.SUCCESS,
                        data: payload,
                    },
                };
            })
            .addCase(fetchDealTerms.rejected.toString(), (state, action) => {
                return {
                    ...state,
                    dealTerms: {
                        status: status.FAILURE,
                        data: action.error,
                    },
                };
            })
            // createDealTerm
            .addCase(createDealTerm.pending.toString(), (state, action) => {
                return {
                    ...state,
                    createTerm: {
                        status: status.IN_PROGRESS,
                        data: {},
                    },
                };
            })
            .addCase(createDealTerm.fulfilled.toString(), (state, { payload }) => {
                return {
                    ...state,
                    createTerm: {
                        status: status.SUCCESS,
                        data: payload,
                    },
                };
            })
            .addCase(createDealTerm.rejected.toString(), (state, action) => {
                return {
                    ...state,
                    createTerm: {
                        status: status.FAILURE,
                        data: action.error,
                    },
                };
            })
            // updateDealTerm
            .addCase(updateDealTerm.pending.toString(), (state, action) => {
                return {
                    ...state,
                    updateTerm: {
                        status: status.IN_PROGRESS,
                        data: {},
                    },
                };
            })
            .addCase(updateDealTerm.fulfilled.toString(), (state, { payload }) => {
                return {
                    ...state,
                    updateTerm: {
                        status: status.SUCCESS,
                        data: payload,
                    },
                };
            })
            .addCase(updateDealTerm.rejected.toString(), (state, action) => {
                return {
                    ...state,
                    updateTerm: {
                        status: status.FAILURE,
                        data: action.error,
                    },
                };
            })
            // deleteDealTerm
            .addCase(deleteDealTerm.pending.toString(), (state, action) => {
                return {
                    ...state,
                    deleteTerm: {
                        status: status.IN_PROGRESS,
                        data: {},
                    },
                };
            })
            .addCase(deleteDealTerm.fulfilled.toString(), (state, { payload }) => {
                return {
                    ...state,
                    deleteTerm: {
                        status: status.SUCCESS,
                        data: payload,
                    },
                };
            })
            .addCase(deleteDealTerm.rejected.toString(), (state, action) => {
                return {
                    ...state,
                    deleteTerm: {
                        status: status.FAILURE,
                        data: action.error,
                    },
                };
            })
            // createDeal
            .addCase(createDeal.pending.toString(), (state, action) => {
                return {
                    ...state,
                    createDeal: {
                        status: status.IN_PROGRESS,
                        data: {},
                    },
                };
            })
            .addCase(createDeal.fulfilled.toString(), (state, { payload }) => {
                return {
                    ...state,
                    createDeal: {
                        status: status.SUCCESS,
                        data: payload,
                    },
                };
            })
            .addCase(createDeal.rejected.toString(), (state, action) => {
                return {
                    ...state,
                    createDeal: {
                        status: status.FAILURE,
                        data: action.payload || action.error,
                    },
                };
            })
            // updateDeal
            .addCase(updateDeal.pending.toString(), (state, action) => {
                return {
                    ...state,
                    updateDeal: {
                        status: status.IN_PROGRESS,
                        data: {},
                    },
                };
            })
            .addCase(updateDeal.fulfilled.toString(), (state, { payload }) => {
                return {
                    ...state,
                    updateDeal: {
                        status: status.SUCCESS,
                        data: payload,
                    },
                };
            })
            .addCase(updateDeal.rejected.toString(), (state, action) => {
                return {
                    ...state,
                    updateDeal: {
                        status: status.FAILURE,
                        data: action.payload || action.error,
                    },
                };
            })
            // getDealById
            .addCase(getDealById.pending.toString(), (state, action) => {
                return {
                    ...state,
                    dealDetails: {
                        status: status.IN_PROGRESS,
                        data: null,
                    },
                };
            })
            .addCase(getDealById.fulfilled.toString(), (state, { payload }) => {
                return {
                    ...state,
                    dealDetails: {
                        status: status.SUCCESS,
                        data: payload,
                    },
                };
            })
            .addCase(getDealById.rejected.toString(), (state, action) => {
                return {
                    ...state,
                    dealDetails: {
                        status: status.FAILURE,
                        data: null,
                    },
                };
            })
            // getInfluencerDealById
            .addCase(getInfluencerDealById.pending.toString(), (state, action) => {
                return {
                    ...state,
                    dealDetails: {
                        status: status.IN_PROGRESS,
                        data: null,
                    },
                };
            })
            .addCase(getInfluencerDealById.fulfilled.toString(), (state, { payload }) => {
                return {
                    ...state,
                    dealDetails: {
                        status: status.SUCCESS,
                        data: payload,
                    },
                };
            })
            .addCase(getInfluencerDealById.rejected.toString(), (state, action) => {
                return {
                    ...state,
                    dealDetails: {
                        status: status.FAILURE,
                        data: null,
                    },
                };
            })
            // createDealDraft
            .addCase(createDealDraft.pending.toString(), (state) => {
                state.draft = { ...state.draft, saveStatus: status.IN_PROGRESS };
            })
            .addCase(createDealDraft.fulfilled.toString(), (state, { payload }) => {
                state.draft = {
                    ...state.draft,
                    draftId: payload?.id ?? state.draft.draftId,
                    saveStatus: status.SUCCESS,
                };
            })
            .addCase(createDealDraft.rejected.toString(), (state) => {
                state.draft = { ...state.draft, saveStatus: status.FAILURE };
            })
            // updateDealDraft
            .addCase(updateDealDraft.pending.toString(), (state) => {
                state.draft = { ...state.draft, saveStatus: status.IN_PROGRESS };
            })
            .addCase(updateDealDraft.fulfilled.toString(), (state) => {
                state.draft = { ...state.draft, saveStatus: status.SUCCESS };
            })
            .addCase(updateDealDraft.rejected.toString(), (state) => {
                state.draft = { ...state.draft, saveStatus: status.FAILURE };
            })
            // getDealDraftById
            .addCase(getDealDraftById.fulfilled.toString(), (state, { payload }) => {
                if (payload?.id) {
                    state.draft = { ...state.draft, draftId: payload.id };
                }
            })
            // fetchMerchantDeals (drafts list)
            .addCase(fetchMerchantDeals.pending.toString(), (state) => {
                state.drafts = { ...state.drafts, status: status.IN_PROGRESS };
            })
            .addCase(fetchMerchantDeals.fulfilled.toString(), (state, { payload }) => {
                state.drafts = {
                    status: status.SUCCESS,
                    data: payload?.deals || [],
                };
            })
            .addCase(fetchMerchantDeals.rejected.toString(), (state) => {
                state.drafts = { ...state.drafts, status: status.FAILURE };
            })
            // deleteDealDraft — drop deleted item from drafts list
            .addCase(deleteDealDraft.fulfilled.toString(), (state, { payload }) => {
                if (payload?.draftId && Array.isArray(state.drafts.data)) {
                    state.drafts.data = state.drafts.data.filter((d) => d.id !== payload.draftId);
                }
            })
            // fetchDealAnalytics
            .addCase(fetchDealAnalytics.pending.toString(), (state, action) => {
                return {
                    ...state,
                    dealAnalytics: {
                        status: status.IN_PROGRESS,
                        data: null,
                    },
                };
            })
            .addCase(fetchDealAnalytics.fulfilled.toString(), (state, { payload }) => {
                return {
                    ...state,
                    dealAnalytics: {
                        status: status.SUCCESS,
                        data: payload,
                    },
                };
            })
            .addCase(fetchDealAnalytics.rejected.toString(), (state, action) => {
                return {
                    ...state,
                    dealAnalytics: {
                        status: status.FAILURE,
                        data: null,
                    },
                };
            });
    },
});

export const {
    clearDealTypes,
    clearCategories,
    clearDealTerms,
    clearCreateDeal,
    clearUpdateDeal,
    clearDealDetails,
    setDraftSnapshot,
    clearDraftSnapshot,
} = dealsSlice.actions;
export default dealsSlice.reducer;
